import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmationModal from "@/components/ConfirmationModal";
import { PlayerAvatar } from "@/components/players/PlayerAvatar";
import { EmptyState } from "@/components/ui/empty-state";
import { PageLoader } from "@/components/ui/page-loader";
import { MobileTable } from "@/components/ui/mobile-table";
import { useMediaQuery } from "@/hooks/use-media-query";
import {
  FaPlus,
  FaEdit,
  FaTrashAlt,
  FaSpinner,
  FaUserPlus,
  FaStar,
  FaChartLine,
  FaArrowLeft,
  FaUsers,
  FaChevronRight,
  FaUserCheck,
} from "react-icons/fa";
import { GiFootprint, GiBrain } from "react-icons/gi";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/lib/api";
import {
  fetchGroupGuests,
  promoteGuestToPlayer,
  GuestPromotionError,
  type GuestRsvpSummary,
} from "@/lib/api/guestPromotion";
import { fetchGuestProfiles } from "@/lib/api/guestProfiles";
import { shouldSuggestGuestPromotion, type GuestProfileCache } from "@/lib/v2/guestBalance";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { useGroup } from "@/lib/context/GroupContext";
import { PermissionGate } from "@/components/groups/PermissionGate";
import { FloatingActionButton } from "@/components/ui/floating-action-button";
import { useGroupPermissions } from "@/hooks/use-group-permissions";
import RatingSuggestionBanner from "@/components/players/RatingSuggestionBanner";
import {
  suggestRatingAdjustments,
  getGroupAverageRatings,
  RatingSuggestion,
} from "@/utils/ratingSuggestions";
import { calculatePlayerRating } from "@/utils/playerRating";
import { PlayerAccountBadge } from "@/components/v2/squad/PlayerAccountBadge";

export interface Player {
  id: number;
  created_at: string;
  name: string;
  skills: number;
  effort: number;
  stamina: number;
  group_id?: string;
  rating?: number;
  avatar_url?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface PlayersRosterProps {
  headingKey?: string;
  descriptionKey?: string;
  fetchErrorKey?: string;
  /** V2: list → profile detail → edit */
  profileMode?: boolean;
}

const getDefaultFormData = (players: Player[]) => {
  const avg = getGroupAverageRatings(players);
  return { name: "", skills: avg.skills, effort: avg.effort, stamina: avg.stamina };
};

function RatingStat({
  icon: Icon,
  label,
  value,
  iconClass,
}: {
  icon: typeof GiFootprint;
  label: string;
  value: number;
  iconClass: string;
}) {
  return (
    <div className="rounded-lg border bg-accent/30 p-3 text-center space-y-1">
      <Icon className={`h-4 w-4 mx-auto ${iconClass}`} />
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
}

export default function PlayersRoster({
  headingKey = "players.title",
  descriptionKey,
  fetchErrorKey = "players.fetchError",
  profileMode = false,
}: PlayersRosterProps) {
  const { t } = useTranslation();
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [profilePlayer, setProfilePlayer] = useState<Player | null>(null);
  const [formData, setFormData] = useState(getDefaultFormData([]));
  const [matches, setMatches] = useState<Record<string, unknown>[]>([]);
  const [suggestions, setSuggestions] = useState<Record<number, RatingSuggestion>>({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState<Set<number>>(new Set());
  const [guests, setGuests] = useState<GuestRsvpSummary[]>([]);
  const [guestProfiles, setGuestProfiles] = useState<GuestProfileCache>({});
  const [promoteGuest, setPromoteGuest] = useState<GuestRsvpSummary | null>(null);
  const { canEdit } = useGroupPermissions();
  const { toast } = useEnhancedToast();
  const { currentGroup } = useGroup();

  const groupId = currentGroup?.id;

  const gamesPlayedById = useMemo(() => {
    const counts = new Map<number, number>();
    for (const match of matches) {
      const teama = (match.teama as number[] | undefined) ?? [];
      const teamb = (match.teamb as number[] | undefined) ?? [];
      for (const id of [...teama, ...teamb]) {
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }
    }
    return counts;
  }, [matches]);

  const load = async () => {
    if (!groupId) {
      setPlayers([]);
      setMatches([]);
      setGuests([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const [playersData, matchesData, guestData, guestCache] = await Promise.all([
        API.getPlayers(groupId),
        API.getMatches(groupId),
        profileMode ? fetchGroupGuests(groupId) : Promise.resolve([]),
        profileMode ? fetchGuestProfiles(groupId) : Promise.resolve({}),
      ]);
      setPlayers(playersData as Player[]);
      setMatches(matchesData as Record<string, unknown>[]);
      setGuests(guestData);
      setGuestProfiles(guestCache);
    } catch (error) {
      console.error("Error fetching players:", error);
      toast({
        title: t("errors.error"),
        description: t(fetchErrorKey),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [groupId]);

  useEffect(() => {
    const next: Record<number, RatingSuggestion> = {};
    for (const player of players) {
      if (dismissedSuggestions.has(player.id)) continue;
      const suggestion = suggestRatingAdjustments(player.id, matches, {
        skills: player.skills,
        effort: player.effort,
        stamina: player.stamina,
      });
      if (suggestion) next[player.id] = suggestion;
    }
    setSuggestions(next);
  }, [players, matches, dismissedSuggestions]);

  const handleAddPlayer = () => {
    setFormData(getDefaultFormData(players));
    setSelectedPlayer(null);
    setIsAddModalOpen(true);
  };

  const handleEditPlayer = (player: Player) => {
    setFormData({
      name: player.name,
      skills: Number(player.skills),
      effort: Number(player.effort),
      stamina: Number(player.stamina),
    });
    setSelectedPlayer(player);
    setIsAddModalOpen(true);
  };

  const handleRowClick = (player: Player) => {
    if (profileMode) {
      setProfilePlayer(player);
    } else {
      handleEditPlayer(player);
    }
  };

  const handleDeleteClick = (player: Player) => {
    setSelectedPlayer(player);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedPlayer || !groupId) return;

    setIsProcessing(true);
    try {
      await API.deletePlayer(selectedPlayer.id, groupId);
      setPlayers((prev) => prev.filter((p) => p.id !== selectedPlayer.id));
      if (profilePlayer?.id === selectedPlayer.id) setProfilePlayer(null);
      toast({
        title: t("players.deleteSuccess"),
        description: t("players.playerRemoved", { name: selectedPlayer.name }),
      });
      setSelectedPlayer(null);
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error("Error deleting player:", error);
      toast({
        title: t("players.deleteError"),
        description: error instanceof Error ? error.message : t("errors.error"),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      toast({
        title: t("errors.validationError"),
        description: t("players.nameRequired"),
        variant: "destructive",
      });
      return;
    }
    if (!groupId) {
      toast({
        title: t("errors.error"),
        description: t("groups.noGroupSelected"),
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    const playerData = {
      name: formData.name.trim(),
      skills: Math.max(1, Math.min(100, Math.round(formData.skills))),
      effort: Math.max(1, Math.min(100, Math.round(formData.effort))),
      stamina: Math.max(1, Math.min(100, Math.round(formData.stamina))),
      group_id: groupId,
    };

    try {
      if (selectedPlayer) {
        const updated = (await API.updatePlayer(selectedPlayer.id, playerData)) as Player;
        const withRating = { ...updated, rating: calculatePlayerRating(updated) };
        setPlayers((prev) =>
          prev
            .map((p) => (p.id === updated.id ? withRating : p))
            .sort((a, b) => a.name.localeCompare(b.name))
        );
        if (profilePlayer?.id === updated.id) setProfilePlayer(withRating);
        toast({ title: t("players.updateSuccess") });
      } else {
        const created = (await API.createPlayer(playerData)) as Player;
        const withRating = { ...created, rating: calculatePlayerRating(created) };
        setPlayers((prev) =>
          [...prev, withRating].sort((a, b) => a.name.localeCompare(b.name))
        );
        toast({ title: t("players.addSuccess") });
      }
      setFormData(getDefaultFormData(players));
      setSelectedPlayer(null);
      setIsAddModalOpen(false);
    } catch (error) {
      console.error("Error saving player:", error);
      toast({
        title: selectedPlayer ? t("players.updateError") : t("players.addError"),
        description: error instanceof Error ? error.message : t("errors.error"),
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handlePromoteGuestConfirm = async () => {
    if (!promoteGuest || !groupId) return;

    setIsProcessing(true);
    try {
      const created = (await promoteGuestToPlayer(groupId, promoteGuest.guestName)) as Player;
      const withRating = { ...created, rating: calculatePlayerRating(created) };
      setPlayers((prev) =>
        [...prev, withRating].sort((a, b) => a.name.localeCompare(b.name))
      );
      setGuests((prev) => prev.filter((g) => g.guestName !== promoteGuest.guestName));
      setPromoteGuest(null);
      toast({
        title: t("v2.squad.guests.promoteSuccess"),
        description: t("v2.squad.guests.promoteSuccessDesc", { name: created.name }),
      });
    } catch (error) {
      const message =
        error instanceof GuestPromotionError
          ? error.code === "PLAYER_EXISTS"
            ? t("v2.squad.guests.playerExists")
            : t("v2.squad.guests.promoteError")
          : error instanceof Error
            ? error.message
            : t("errors.error");
      toast({
        title: t("v2.squad.guests.promoteError"),
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const approveSuggestion = async (suggestion: RatingSuggestion) => {
    const player = players.find((p) => p.id === suggestion.playerId);
    if (!player || !groupId) return;

    const updates: Partial<Player> = { name: player.name };
    if (suggestion.skills) updates.skills = suggestion.skills.to;
    if (suggestion.effort) updates.effort = suggestion.effort.to;
    if (suggestion.stamina) updates.stamina = suggestion.stamina.to;

    try {
      const updated = (await API.updatePlayer(player.id, updates)) as Player;
      setDismissedSuggestions((prev) => new Set(prev).add(player.id));
      const withRating = { ...updated, rating: calculatePlayerRating(updated) };
      setPlayers((prev) => prev.map((p) => (p.id === player.id ? withRating : p)));
      if (profilePlayer?.id === player.id) setProfilePlayer(withRating);
      toast({
        title: t("common.success"),
        description: t("players.ratingSuggestion.ratingsUpdated"),
      });
    } catch (error) {
      toast({
        title: t("errors.error"),
        description: error instanceof Error ? error.message : t("errors.error"),
        variant: "destructive",
      });
    }
  };

  const mobileColumns = [
    {
      header: t("players.name"),
      accessorKey: "name" as keyof Player,
      priority: "high" as const,
      cell: (player: Player) => (
        <div className="flex items-center gap-1">
          <PlayerAvatar
            playerId={player.id}
            playerName={player.name}
            avatarUrl={player.avatar_url}
            size="sm"
            editable={false}
          />
          <span className="font-medium text-xs truncate max-w-[80px]">{player.name}</span>
        </div>
      ),
      meta: { width: "40%" },
    },
    {
      header: t("players.skills", "S"),
      accessorKey: "skills" as keyof Player,
      priority: "high" as const,
      cell: (player: Player) => (
        <div className="flex items-center justify-center">
          <GiFootprint className="h-3 w-3 text-blue-500 mr-1" />
          <span>{player.skills}</span>
        </div>
      ),
      meta: { align: "center" as const, width: "20%" },
    },
    {
      header: t("players.effort", "E"),
      accessorKey: "effort" as keyof Player,
      priority: "high" as const,
      cell: (player: Player) => (
        <div className="flex items-center justify-center">
          <GiBrain className="h-3 w-3 text-green-500 mr-1" />
          <span>{player.effort}</span>
        </div>
      ),
      meta: { align: "center" as const, width: "20%" },
    },
    {
      header: t("players.stamina", "S"),
      accessorKey: "stamina" as keyof Player,
      priority: "high" as const,
      cell: (player: Player) => (
        <div className="flex items-center justify-center">
          <FaChartLine className="h-3 w-3 text-red-500 mr-1" />
          <span>{player.stamina}</span>
        </div>
      ),
      meta: { align: "center" as const, width: "20%" },
    },
    {
      header: t("players.rating"),
      accessorKey: "rating" as keyof Player,
      priority: "high" as const,
      cell: (player: Player) => (
        <div className="flex items-center justify-center">
          <div className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 rounded-full px-2 py-0.5 text-xs font-medium">
            {player.rating ?? calculatePlayerRating(player)}
          </div>
        </div>
      ),
      meta: { align: "center" as const, width: "20%" },
    },
  ];

  const emptyState = (
    <EmptyState
      icon={FaUserPlus}
      title={t("dashboard.noPlayersAdded")}
      description={t("players.noPlayersDescription")}
      actionLabel={t("dashboard.addFirstPlayer")}
      actionIcon={FaPlus}
      onAction={handleAddPlayer}
      className="border-0 py-8"
    />
  );

  const renderProfile = () => {
    if (!profilePlayer) return null;
    const rating = profilePlayer.rating ?? calculatePlayerRating(profilePlayer);
    const games = gamesPlayedById.get(profilePlayer.id) ?? 0;
    const suggestion = suggestions[profilePlayer.id];

    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setProfilePlayer(null)}
          className="gap-2 -ml-2"
        >
          <FaArrowLeft /> {t("v2.squad.profile.back")}
        </Button>

        <Card>
          <CardHeader className="items-center text-center pb-2">
            <PlayerAvatar
              playerId={profilePlayer.id}
              playerName={profilePlayer.name}
              avatarUrl={profilePlayer.avatar_url}
              size="lg"
              editable={canEdit}
              onAvatarChange={(url) => {
                const updated = { ...profilePlayer, avatar_url: url };
                setProfilePlayer(updated);
                setPlayers((prev) =>
                  prev.map((p) => (p.id === profilePlayer.id ? updated : p))
                );
              }}
            />
            <CardTitle className="text-xl mt-3">{profilePlayer.name}</CardTitle>
            <PlayerAccountBadge metadata={profilePlayer.metadata} size="md" />
            <div className="inline-flex items-center gap-1 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 px-3 py-1 text-sm font-semibold">
              <FaStar className="h-3 w-3" />
              {rating}
            </div>
            <p className="text-sm text-muted-foreground">
              {t("v2.squad.profile.gamesPlayed", { count: games })}
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <RatingStat
                icon={GiFootprint}
                label={t("players.skills")}
                value={profilePlayer.skills}
                iconClass="text-blue-500"
              />
              <RatingStat
                icon={GiBrain}
                label={t("players.effort")}
                value={profilePlayer.effort}
                iconClass="text-green-500"
              />
              <RatingStat
                icon={FaChartLine}
                label={t("players.stamina")}
                value={profilePlayer.stamina}
                iconClass="text-red-500"
              />
            </div>

            {suggestion && (
              <RatingSuggestionBanner
                suggestion={suggestion}
                onApprove={() => approveSuggestion(suggestion)}
                onDismiss={() =>
                  setDismissedSuggestions((prev) => new Set(prev).add(profilePlayer.id))
                }
              />
            )}

            <PermissionGate requireEdit>
              <div className="flex gap-2 pt-2">
                <Button
                  className="flex-1 bg-soccer-primary text-black hover:bg-soccer-primary/90 min-h-[44px]"
                  onClick={() => handleEditPlayer(profilePlayer)}
                >
                  <FaEdit className="mr-2" />
                  {t("v2.squad.profile.editRatings")}
                </Button>
                <Button
                  variant="destructive"
                  className="min-h-[44px]"
                  onClick={() => handleDeleteClick(profilePlayer)}
                >
                  <FaTrashAlt />
                </Button>
              </div>
            </PermissionGate>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderGuestsSection = () => {
    if (!profileMode || guests.length === 0) return null;

    return (
      <div className="space-y-2 pt-2">
        <div>
          <h2 className="text-sm font-semibold">{t("v2.squad.guests.title")}</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {t("v2.squad.guests.description")}
          </p>
        </div>
        {guests.map((guest) => {
          const suggestPromote = shouldSuggestGuestPromotion(
            guest.guestName,
            guestProfiles,
            guest.rsvpCount
          );
          return (
          <div
            key={guest.guestName}
            className="flex items-center gap-3 rounded-lg border border-dashed bg-muted/30 p-3"
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{guest.guestName}</p>
              <p className="text-xs text-muted-foreground">
                {t("v2.squad.guests.rsvpCount", { count: guest.rsvpCount })}
              </p>
              {suggestPromote && (
                <p className="text-xs font-medium text-soccer-primary mt-0.5">
                  {t("v2.squad.guests.promoteSuggested", { count: guest.rsvpCount })}
                </p>
              )}
            </div>
            <PermissionGate requireEdit>
              <Button
                variant={suggestPromote ? "default" : "outline"}
                size="sm"
                className="shrink-0 min-h-[44px]"
                onClick={() => setPromoteGuest(guest)}
                disabled={isProcessing}
              >
                <FaUserCheck className="mr-2 h-3 w-3" />
                {t("v2.squad.guests.promote")}
              </Button>
            </PermissionGate>
          </div>
        );
        })}
      </div>
    );
  };

  const renderV2List = () => (
    <div className="space-y-4">
      {renderGuestsSection()}
      <div className="space-y-2">
      {players.map((player) => (
        <button
          type="button"
          key={player.id}
          onClick={() => handleRowClick(player)}
          className="w-full text-left rounded-lg border bg-card p-3 hover:border-soccer-primary/50 transition-colors min-h-[44px]"
        >
          <div className="flex items-center gap-3">
            <PlayerAvatar
              playerId={player.id}
              playerName={player.name}
              avatarUrl={player.avatar_url}
              size="sm"
              editable={false}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <p className="font-semibold truncate">{player.name}</p>
                <PlayerAccountBadge metadata={player.metadata} />
              </div>
              <p className="text-xs text-muted-foreground">
                {t("v2.squad.profile.ratingShort", {
                  rating: player.rating ?? calculatePlayerRating(player),
                })}
              </p>
            </div>
            <FaChevronRight className="text-muted-foreground shrink-0" />
          </div>
        </button>
      ))}
      </div>
    </div>
  );

  const modals = (
    <>
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {selectedPlayer ? t("players.editPlayer") : t("players.addPlayer")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-center py-2">
            <PlayerAvatar
              playerId={selectedPlayer?.id || 0}
              playerName={formData.name || "New Player"}
              avatarUrl={selectedPlayer?.avatar_url}
              size="lg"
              editable={!!selectedPlayer}
              onAvatarChange={(url) => {
                if (selectedPlayer) {
                  const updated = { ...selectedPlayer, avatar_url: url };
                  setSelectedPlayer(updated);
                  setPlayers((prev) =>
                    prev.map((p) => (p.id === selectedPlayer.id ? updated : p))
                  );
                  if (profilePlayer?.id === selectedPlayer.id) setProfilePlayer(updated);
                }
              }}
            />
          </div>
          <form onSubmit={handleFormSubmit}>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="name" className="text-right">
                  {t("players.name")}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="col-span-3"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="skills" className="text-right flex gap-1 items-center justify-end">
                  <GiFootprint className="h-4 w-4" /> {t("players.skills")}
                </Label>
                <Input
                  id="skills"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.skills}
                  onChange={(e) => setFormData({ ...formData, skills: Number(e.target.value) })}
                  className="col-span-3"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="effort" className="text-right flex gap-1 items-center justify-end">
                  <GiBrain className="h-4 w-4" /> {t("players.effort")}
                </Label>
                <Input
                  id="effort"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.effort}
                  onChange={(e) => setFormData({ ...formData, effort: Number(e.target.value) })}
                  className="col-span-3"
                  required
                  disabled={isProcessing}
                />
              </div>
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="stamina" className="text-right flex gap-1 items-center justify-end">
                  <FaChartLine className="h-4 w-4" /> {t("players.stamina")}
                </Label>
                <Input
                  id="stamina"
                  type="number"
                  min={1}
                  max={100}
                  value={formData.stamina}
                  onChange={(e) => setFormData({ ...formData, stamina: Number(e.target.value) })}
                  className="col-span-3"
                  required
                  disabled={isProcessing}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsAddModalOpen(false)}
                disabled={isProcessing}
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                className="bg-soccer-primary hover:bg-soccer-primary/90"
                disabled={isProcessing}
              >
                {isProcessing && <FaSpinner className="mr-2 h-4 w-4 animate-spin" />}
                {selectedPlayer ? t("common.saveChanges") : t("players.addPlayer")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t("players.deletePlayer")}
        message={t("players.deleteConfirmMessage", { name: selectedPlayer?.name })}
        confirmText={isProcessing ? t("players.deleting") : t("common.delete")}
        isDestructive
        isProcessing={isProcessing}
      />

      <ConfirmationModal
        isOpen={!!promoteGuest}
        onClose={() => setPromoteGuest(null)}
        onConfirm={handlePromoteGuestConfirm}
        title={t("v2.squad.guests.promoteTitle")}
        message={t("v2.squad.guests.promoteConfirm", { name: promoteGuest?.guestName })}
        confirmText={
          isProcessing ? t("v2.squad.guests.promoting") : t("v2.squad.guests.promote")
        }
        isProcessing={isProcessing}
      />

      {canEdit && !isAddModalOpen && !(profileMode && profilePlayer) && (
        <FloatingActionButton onClick={handleAddPlayer} label={t("players.addPlayer")} />
      )}
    </>
  );

  if (!groupId) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {t("groups.noGroupSelected")}
      </p>
    );
  }

  if (profileMode && profilePlayer) {
    return (
      <TooltipProvider>
        {renderProfile()}
        {modals}
      </TooltipProvider>
    );
  }

  return (
    <>
      <TooltipProvider>
        <div className="space-y-4">
          <div className="flex justify-between items-start gap-2">
            <div>
              <h1
                className={`font-bold flex items-center gap-2 ${profileMode ? "text-2xl" : "text-2xl"}`}
              >
                {profileMode && <FaUsers className="text-soccer-primary" />}
                {t(headingKey)}
              </h1>
              {descriptionKey && (
                <p className="text-sm text-muted-foreground mt-1">{t(descriptionKey)}</p>
              )}
            </div>
            <PermissionGate requireEdit>
              <Button
                onClick={handleAddPlayer}
                className="bg-soccer-primary hover:bg-soccer-primary/90 shrink-0"
                disabled={loading}
              >
                <FaPlus className="mr-2 h-4 w-4" /> {t("players.addPlayer")}
              </Button>
            </PermissionGate>
          </div>

          {!profileMode &&
            Object.values(suggestions)
              .slice(0, 3)
              .map((suggestion) => (
                <RatingSuggestionBanner
                  key={suggestion.playerId}
                  suggestion={suggestion}
                  onApprove={() => approveSuggestion(suggestion)}
                  onDismiss={() =>
                    setDismissedSuggestions((prev) => new Set(prev).add(suggestion.playerId))
                  }
                />
              ))}

          {loading ? (
            <PageLoader label={t("common.loading")} />
          ) : players.length === 0 ? (
            emptyState
          ) : profileMode ? (
            renderV2List()
          ) : isMobile ? (
            <MobileTable
              data={players}
              columns={mobileColumns}
              keyExtractor={(player) => player.id}
              onRowClick={handleRowClick}
              emptyState={emptyState}
              isLoading={loading}
            />
          ) : (
            <div className="rounded-md border bg-card border-border table-scroll-container">
              <Table>
                <TableHeader>
                  <TableRow>
                    {!profileMode && <TableHead className="w-[50px] text-center">ID</TableHead>}
                    <TableHead className="w-[200px]">{t("players.name")}</TableHead>
                    <TableHead className="w-[100px] text-center">
                      <Tooltip>
                        <TooltipTrigger className="w-full flex items-center justify-center gap-1">
                          <GiFootprint className="h-4 w-4" /> {t("players.skills")}
                        </TooltipTrigger>
                        <TooltipContent>{t("players.skills")}</TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="w-[100px] text-center">
                      <Tooltip>
                        <TooltipTrigger className="w-full flex items-center justify-center gap-1">
                          <GiBrain className="h-4 w-4" /> {t("players.effort")}
                        </TooltipTrigger>
                        <TooltipContent>{t("players.effort")}</TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="w-[100px] text-center">
                      <Tooltip>
                        <TooltipTrigger className="w-full flex items-center justify-center gap-1">
                          <FaChartLine className="h-4 w-4" /> {t("players.stamina")}
                        </TooltipTrigger>
                        <TooltipContent>{t("players.stamina")}</TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="w-[100px] text-center">
                      <Tooltip>
                        <TooltipTrigger className="w-full flex items-center justify-center gap-1">
                          <FaStar className="h-4 w-4" /> {t("players.rating")}
                        </TooltipTrigger>
                        <TooltipContent>{t("players.averageRating")}</TooltipContent>
                      </Tooltip>
                    </TableHead>
                    <TableHead className="w-[120px] text-right">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {players.map((player) => (
                    <TableRow
                      key={player.id}
                      className={profileMode ? "cursor-pointer" : undefined}
                      onClick={profileMode ? () => handleRowClick(player) : undefined}
                    >
                      {!profileMode && (
                        <TableCell className="w-[50px]">
                          <div className="text-center w-full">{player.id}</div>
                        </TableCell>
                      )}
                      <TableCell className="w-[200px] font-medium">
                        <div className="flex items-center gap-2">
                          <PlayerAvatar
                            playerId={player.id}
                            playerName={player.name}
                            avatarUrl={player.avatar_url}
                            size="sm"
                            editable={false}
                          />
                          <span>{player.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="w-[100px]">
                        <div className="text-center w-full">{player.skills}</div>
                      </TableCell>
                      <TableCell className="w-[100px]">
                        <div className="text-center w-full">{player.effort}</div>
                      </TableCell>
                      <TableCell className="w-[100px]">
                        <div className="text-center w-full">{player.stamina}</div>
                      </TableCell>
                      <TableCell className="w-[100px]">
                        <div className="text-center w-full">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100">
                            {player.rating ?? calculatePlayerRating(player)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="w-[120px] text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPlayer(player);
                            }}
                            disabled={isProcessing}
                          >
                            <FaEdit className="h-4 w-4" />
                            <span className="sr-only">{t("common.edit")}</span>
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(player);
                            }}
                            disabled={isProcessing}
                          >
                            <FaTrashAlt className="h-4 w-4" />
                            <span className="sr-only">{t("common.delete")}</span>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </TooltipProvider>
      {modals}
    </>
  );
}
